import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Send an idle kiosk back to its home screen.
 *
 * The kiosk is a shared terminal bolted to the front desk. Once a customer
 * is in the chair, whatever is on screen is stale — the next person walking
 * in needs a check-in flow, not somebody else's confirmation.
 *
 * Deliberately NOT used while a customer is waiting in line: they may be
 * standing there watching for their turn, and yanking the screen out from
 * under them would be worse than a stale display.
 *
 * @param enabled  Arm the timer. Pass false for states that must persist.
 * @param onIdleTo Route to return to.
 * @param delayMs  Idle window. Resets on any interaction.
 */
export function useIdleReturn(enabled: boolean, onIdleTo = '/kiosk', delayMs = 60_000) {
  const navigate = useNavigate();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /** Restart the countdown. Wire to onClick/onTouchStart at the screen root. */
  const reset = useCallback(() => {
    clear();
    if (!enabled) return;
    timer.current = setTimeout(() => navigate(onIdleTo), delayMs);
  }, [clear, enabled, navigate, onIdleTo, delayMs]);

  // Starts fresh on mount and whenever `enabled` flips — so a customer who
  // transitions from waiting to cutting gets a full window, not a remainder.
  useEffect(() => {
    reset();
    return clear;
  }, [reset, clear]);

  return { resetIdleTimer: reset };
}

export default useIdleReturn;
